import React, { useState } from 'react';
import { researchStatusLabel } from '../utils/researchStatus';
import { currentLocale, targetingLabel } from '../i18n';
import { useTranslation } from 'react-i18next';
import ResearchAIAnalysisPanel from './ResearchAIAnalysisPanel';
import {
  ResearchData, DonutCard, Donut, HorizontalBarChart, StackedBarChart,
  ANSWER_COLORS, AGE_COLORS, GENDER_COLORS, GEO_COLORS, imgUrl, fmtDate,
} from './ResearchDievoCharts';
import { describeTargeting } from '../utils/researchTargeting';
import '../styles/research-share-dievo.css';

/* In-app results view, DIEVO edition — the same design as the Share page, with
   the controls the Share page has no business showing (pause/resume, export,
   AI analysis with translation). ResearchDashboard owns the fetch and the
   handlers and renders this only when edition === 'dievo'. */

interface Props {
  data: ResearchData;
  shareToken?: string;
  aiAnalysisUrl: string;
  aiTranslateUrl: string;
  onPause: () => void;
  onResume: () => void;
  saving: boolean;
  exporting: boolean;
  onExportExcel: () => void;
}

const WarmupPlaceholder: React.FC<{ title: string; text: string }> = ({ title, text }) => (
  <div className="rsd-warmup-placeholder">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
    <h4>{title}</h4>
    <p>{text}</p>
  </div>
);

const ResearchDashboardDievo: React.FC<Props> = ({
  data, shareToken, aiAnalysisUrl, aiTranslateUrl,
  onPause, onResume, saving, exporting, onExportExcel,
}) => {
  const { t } = useTranslation();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showAudience, setShowAudience] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const progressPercentage = data.respondent_count > 0
    ? Math.min(100, (data.current_respondents / data.respondent_count) * 100) : 0;
  const isWarmup = data.current_respondents === 0;
  // What the research was ordered for, as opposed to who actually answered.
  const audienceSections = describeTargeting((data as any).targeting);

  // Est. completion runs off launched_at (the pacing anchor); the finished line
  // quotes original_launched_at, the date the research actually opened.
  const estEnd = (from?: string) => {
    if (!from || !data.progress) return '';
    const avgDays = (data.progress.duration_days_min + data.progress.duration_days_max) / 2;
    return fmtDate(new Date(new Date(from).getTime() + avgDays * 864e5).toISOString(),
                   { month: 'short', day: 'numeric' });
  };

  return (
    <div className="research-share-v2 rsd-inapp">
      <div className="rsd-share-page">

        <div className="rsd-dashboard-header">
          <div className="rsd-dashboard-header-left">
            <h2 className="rsd-dashboard-title">{data.name}</h2>
            <span className={`rsd-dashboard-status rsd-status-${data.status.toLowerCase()}`}>{researchStatusLabel(data.status)}</span>
          </div>
          <div className="rsd-dashboard-header-right">
            <ResearchAIAnalysisPanel
              researchStatus={data.status}
              getUrl={aiAnalysisUrl}
              translateUrl={aiTranslateUrl}
            />

            {data.status === 'ACTIVE' && (
              <button className="rsd-btn rsd-btn-secondary" onClick={onPause} disabled={saving}>
                {saving ? <span className="rsd-btn-spinner" /> : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                  </svg>
                )}
                {saving ? t('research.pausing') : t('research.pause')}
              </button>
            )}
            {data.status === 'PAUSED' && (
              <button className="rsd-btn rsd-btn-secondary" onClick={onResume} disabled={saving}>
                {saving ? <span className="rsd-btn-spinner" /> : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
                {saving ? t('research.resuming') : t('research.resume')}
              </button>
            )}

            {/* Export stays hidden during warmup — there is nothing to export yet */}
            {!isWarmup && (
              <div className="rsd-export-dropdown">
                <button className="rsd-btn rsd-btn-primary" disabled={exporting}
                  onClick={() => setShowExportMenu(v => !v)}>
                  {exporting ? <span className="rsd-btn-spinner" /> : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  )}
                  {exporting ? t('research.exporting') : t('research.export')}
                </button>
                {showExportMenu && (
                  <>
                    <div className="rsd-export-backdrop" onClick={() => setShowExportMenu(false)} />
                    <div className="rsd-export-menu">
                      <button onClick={() => { setShowExportMenu(false); onExportExcel(); }}>
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h6v6h6v10H6z" /><path d="M8 12h8v2H8zm0 4h8v2H8z" /></svg>
                        {t('research.exportExcel')}
                      </button>
                      {shareToken && (
                        <button onClick={() => { setShowExportMenu(false); window.open(`/research/share/${shareToken}`, '_blank'); }}>
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" /></svg>
                          {t('research.shareResults')}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="rsd-dashboard-progress-section">
          <div className="rsd-progress-info">
            <div className="rsd-progress-numbers">
              <span className="rsd-progress-current">{data.current_respondents.toLocaleString(currentLocale())}</span>
              <span className="rsd-progress-separator">/</span>
              <span className="rsd-progress-target">{data.respondent_count.toLocaleString(currentLocale())}</span>
              <span className="rsd-progress-label">{t('research.respondentsLabel')}</span>
            </div>
            <div className="rsd-progress-right">
              {data.progress && !data.progress.is_complete && isWarmup && (
                <span className="rsd-progress-warmup">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                  {t('research.warmingUp')}
                </span>
              )}
              <span className="rsd-progress-percentage">{progressPercentage.toFixed(1)}%</span>
            </div>
          </div>
          <div className="rsd-progress-bar-container">
            <div className={`rsd-progress-bar-fill${progressPercentage < 100 ? ' rsd-progress-bar-fill--live' : ''}`} style={{ width: `${progressPercentage}%` }} />
          </div>
          {data.progress && !data.progress.is_complete && (
            <div className="rsd-progress-dates">
              <div className="rsd-progress-date-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                <span>{t('research.started')} · <strong>{fmtDate(data.progress.original_launched_at, { month: 'short', day: 'numeric' })}</strong></span>
              </div>
              <div className="rsd-progress-date-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
                <span>{t('research.estCompletion')} · <strong>{estEnd(data.progress.launched_at)}</strong></span>
              </div>
            </div>
          )}
          {data.progress?.is_complete && (
            <div className="rsd-progress-dates">
              <div className="rsd-progress-date-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                <span>{t('research.statusCompleted')} · <strong>
                  {fmtDate(data.progress.original_launched_at, { month: 'short', day: 'numeric' })}
                  {' — '}{estEnd(data.progress.original_launched_at)}
                </strong></span>
              </div>
            </div>
          )}
        </div>

        {/* The brief, collapsed by default — it sits above the passport because
            that is the order the question comes in: who was asked for, then who
            actually answered. */}
        {audienceSections.length > 0 && (
          <div className={`rsd-audience ${showAudience ? 'rsd-open' : ''}`}>
            <button className="rsd-audience-header" onClick={() => setShowAudience(v => !v)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></svg>
              <span className="rsd-audience-title">{t('research.audienceSettings')}</span>
              <span className="rsd-audience-summary">
                {audienceSections.map(s => `${t(s.labelKey)}: ${s.values.length}`).join(' · ')}
              </span>
              <svg className="rsd-audience-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {showAudience && (
              <div className="rsd-audience-body">
                {audienceSections.map(section => (
                  <div key={section.labelKey} className="rsd-audience-row">
                    <div className="rsd-audience-label">{t(section.labelKey)}</div>
                    <div className="rsd-audience-values">
                      {section.values.map(v => <span key={v} className="rsd-audience-chip">{v}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <section className="rsd-dashboard-section">
          <h3 className="rsd-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            {t('research.passport')}
          </h3>
          {isWarmup ? (
            <WarmupPlaceholder title={t('research.collecting')}
              text={t('research.demographicsWarmup')} />
          ) : (
            <div className="rsd-demographics-grid">
              <DonutCard title={t('research.ageDistribution')} data={(data.demographics.age || []).map((item, i) => ({ label: item.range, value: item.count, color: AGE_COLORS[i % AGE_COLORS.length] }))} />
              <DonutCard title={t('research.genderDistribution')} data={(data.demographics.gender || []).map((item, i) => ({ label: targetingLabel(item.label), value: item.count, color: GENDER_COLORS[i % GENDER_COLORS.length] }))} />
              <DonutCard title={t('research.geography')} maxLegendItems={5} data={(data.demographics.geo || []).map((item, i) => ({ label: item.region, value: item.count, color: GEO_COLORS[i % GEO_COLORS.length] }))} />
            </div>
          )}
        </section>

        <section className="rsd-dashboard-section">
          <h3 className="rsd-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><circle cx="12" cy="12" r="10" /><circle cx="12" cy="17" r="0.5" fill="currentColor" /></svg>
            {t('research.questionResults')}
          </h3>
          {isWarmup ? (
            <WarmupPlaceholder title={t('research.waitingResponses')}
              text={t('research.questionsWarmup')} />
          ) : (
            <div className="rsd-questions-results">
              {data.questions.map((question) => {
                const isMulti = question.question_type === 'multiple_choice';
                const isCreative = question.question_type === 'creative_test';
                const isOpen = expanded === question.id;
                return (
                  <article key={question.id} className={`rsd-question-result-card ${isOpen ? 'rsd-open' : ''}`}>
                    <header className="rsd-question-result-header rsd-clickable"
                      onClick={() => setExpanded(isOpen ? null : question.id)}>
                      <div className="rsd-question-number">Q{question.position}</div>
                      <div className="rsd-question-text">{question.question_text}</div>
                      <svg className="rsd-expand-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                    </header>
                    <div className="rsd-question-result-content">
                      <div className={`rsd-question-charts-row ${isMulti ? 'rsd-row-multi' : ''}`}>
                        <div className="rsd-question-pie-section">
                          {isMulti ? (
                            <>
                              <div className="rsd-multiple-choice-label">{t('research.multipleAnswers')}</div>
                              <HorizontalBarChart data={question.answers.map((a, i) => ({ label: a.answer_text, percentage: a.percentage, color: ANSWER_COLORS[i % ANSWER_COLORS.length] }))} />
                            </>
                          ) : (
                            <>
                              <div className="rsd-question-pie-wrapper">
                                <Donut data={question.answers.map((a, i) => ({ value: a.count, color: ANSWER_COLORS[i % ANSWER_COLORS.length] }))} totalLabel={t('research.answersTotal')} size={200} />
                              </div>
                              <div className="rsd-question-legend">
                                {question.answers.map((a, i) => (
                                  <div key={a.id} className="rsd-question-legend-item">
                                    <span className="rsd-legend-color" style={{ background: ANSWER_COLORS[i % ANSWER_COLORS.length] }} />
                                    {isCreative && a.answer_image
                                      ? <img className="rsd-legend-thumb" src={imgUrl(a.answer_image)} alt={`Option ${i + 1}`} />
                                      : <span className="rsd-legend-text">{a.answer_text}</span>}
                                    <span className="rsd-legend-value">{a.count.toLocaleString(currentLocale())} · {a.percentage}%</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        {question.demographic_breakdown.length > 0 && (
                          <div className="rsd-question-breakdown-section">
                            <h5 className="rsd-breakdown-title">{t('research.breakdownDemographics')}</h5>
                            <StackedBarChart breakdown={question.demographic_breakdown} answers={question.answers} />
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

export default ResearchDashboardDievo;
